import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from 'src/components/iconify';

// How often the card asks the server how the job is going, in milliseconds.
const POLL_INTERVAL = 2000;

// The states a job never leaves once it reaches them. 'not_found' is the server no longer knowing the
// job, so there is nothing more to ask about it.
export const FINAL_STATUSES = ['completed', 'failed', 'not_found'];

/** What the card says when the server no longer knows the job, unless the server said it in its own words. */
export const RESULTS_UNAVAILABLE = "Couldn't get the results. Check My Listings before you try again.";

// How the chip counting each kind of outcome looks. The job's copy supplies the word after the
// count, so the same chip reads "2 archived" on one job and "2 delisted" on another.
const OUTCOME_CHIPS = {
  success: { color: 'success', icon: 'solar:check-circle-bold' },
  partial: { color: 'warning', icon: 'solar:danger-triangle-bold' },
  skipped: { color: 'warning', icon: 'solar:danger-triangle-bold' },
  failed: { color: 'error', icon: 'solar:close-circle-bold' },
};

/** The card's words for a bulk archive. */
export const ARCHIVE_JOB_COPY = {
  title: 'Bulk Archive',
  icon: 'solar:archive-bold',
  processingText: 'Archiving...',
  outcomes: { success: 'archived', skipped: 'still for sale', failed: 'failed' },
};

/** The card's words for a bulk delist. */
export const DELIST_JOB_COPY = {
  title: 'Bulk delist',
  icon: 'solar:eye-closed-bold',
  processingText: 'Delisting...',
  outcomes: {
    success: 'delisted',
    partial: 'partly delisted',
    skipped: 'with nothing to delist',
    failed: 'failed',
  },
};

/**
 * The floating card that follows one queued bulk job — an archive, a delist — from start to finish.
 *
 * The mechanics are the same for every job: ask the server how it is going until it reaches a
 * final state, count the outcomes, hand the results over. Only the words differ, and they come
 * from `copy`; `getStatus` is the thunk that fetches this job's status.
 */
export default function BulkJobProgress({ batchId, onComplete, onClose, getStatus, copy }) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [error, setError] = useState(null);

  const isFinal = FINAL_STATUSES.includes(status?.status);

  const fetchStatus = useCallback(async () => {
    if (!batchId) return;

    try {
      const response = await dispatch(getStatus(batchId));
      if (!response) return;

      setStatus(response);
      setError(null);

      if (response.status === 'completed' && response.results) {
        onComplete?.(response.results);
      } else if (response.status === 'failed') {
        setError(response.error || 'Something went wrong. Check My Listings before you try again.');
      } else if (response.status === 'not_found') {
        setError(response.message || RESULTS_UNAVAILABLE);
      }
    } catch (err) {
      console.error('Failed to fetch bulk job status:', err);
      setError(err.message || "Couldn't check how it's going. We'll keep trying.");
    }
  }, [batchId, dispatch, getStatus, onComplete]);

  // The card stays mounted for the life of the app and only its batchId changes between runs, so
  // whatever belongs to the previous run is cleared the moment a new one starts — including a
  // minimised view, which is compact enough for a new job to start in unnoticed.
  useEffect(() => {
    setIsDismissed(false);
    setIsMinimized(false);
    setStatus(null);
    setError(null);
  }, [batchId]);

  // A dismissed card comes back when the job reaches a final state, so the results are seen.
  useEffect(() => {
    if (isDismissed && isFinal) {
      setIsDismissed(false);
    }
  }, [isDismissed, isFinal]);

  // Ask straight away, then keep asking until the job reaches a final state. Nothing is fetched
  // after that: the status thunk records the job's completion, so a repeat would count it twice.
  useEffect(() => {
    if (!batchId || isFinal) return undefined;

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [batchId, fetchStatus, isFinal]);

  // Don't render if no batchId
  if (!batchId) return null;

  const isCompleted = status?.status === 'completed';
  const isFailed = status?.status === 'failed' || status?.status === 'not_found';
  const isProcessing = status?.status === 'processing' || status?.status === 'queued';
  const progress = status?.progress || 0;
  const totalListings = status?.total || 0;

  // How many items ended up in each outcome the job can have, in the copy's order.
  const results = status?.results;
  const outcomeCounts = Object.entries(copy.outcomes).map(([outcome, label]) => ({
    outcome,
    label,
    count: results?.[outcome] || 0,
  }));

  // An item left alone, only partly done, or failed is not a failure of the run — but the run did
  // not do all of what was asked, so the card must not read as a clean success.
  const needsAttention = outcomeCounts.some(({ outcome, count }) => outcome !== 'success' && count > 0);

  // Get status icon and color
  const getStatusDisplay = () => {
    if (isCompleted) {
      if (!needsAttention) {
        return { icon: 'solar:check-circle-bold', color: 'success.main', text: 'Completed' };
      }
      return { icon: 'solar:danger-triangle-bold', color: 'warning.main', text: 'Completed with issues' };
    }
    if (isFailed) {
      return {
        icon: 'solar:close-circle-bold',
        color: 'error.main',
        text: status?.status === 'not_found' ? "Couldn't get the results" : 'Failed'
      };
    }
    if (status?.status === 'processing') {
      return { icon: copy.icon, color: 'info.main', text: copy.processingText };
    }
    return { icon: 'solar:clock-circle-bold', color: 'text.secondary', text: 'Waiting to start' };
  };

  const statusDisplay = getStatusDisplay();

  // Get background color based on status
  const getBgColor = () => {
    if (isFailed) return 'error.lighter';
    if (!isCompleted) return 'warning.lighter';
    if (needsAttention) return 'warning.lighter';
    return 'success.lighter';
  };

  const handleDismissOrClose = () => {
    if (isCompleted || isFailed) {
      onClose();
    } else {
      setIsDismissed(true);
    }
  };

  // Don't render if dismissed (will reappear when job completes)
  if (isDismissed) return null;

  // Render minimized view
  if (isMinimized) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 280
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: getBgColor(),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
            onClick={() => setIsMinimized(false)}
          >
            {isProcessing && <CircularProgress size={16} />}
            {(isCompleted || isFailed) && (
              <Iconify icon={statusDisplay.icon} width={20} sx={{ color: statusDisplay.color }} />
            )}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {copy.title} {(() => {
                if (isCompleted) return 'Complete';
                if (isFailed) return 'Failed';
                return `${progress}%`;
              })()}
            </Typography>
            <IconButton size="small" aria-label="Expand" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
              <Iconify icon="eva:expand-outline" width={18} />
            </IconButton>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismissOrClose(); }}>
              <Iconify icon="eva:close-outline" width={18} />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    );
  }

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1300,
          borderRadius: 2,
          overflow: 'hidden',
          width: 340,
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: getBgColor(),
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Iconify icon={copy.icon} width={24} sx={{ color: statusDisplay.color }} />
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            {copy.title}
          </Typography>
          <IconButton size="small" aria-label="Minimize" onClick={() => setIsMinimized(true)}>
            <Iconify icon="eva:minus-outline" width={18} />
          </IconButton>
          <IconButton size="small" onClick={handleDismissOrClose}>
            <Iconify icon="eva:close-outline" width={18} />
          </IconButton>
        </Box>

        {/* Progress bar (only when processing) */}
        {isProcessing && (
          <LinearProgress variant="determinate" value={progress} sx={{ height: 4 }} />
        )}

        {/* Content */}
        <CardContent sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          {/* Error message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Status summary */}
          <Stack spacing={2}>
            {/* Progress stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {isProcessing && <CircularProgress size={16} />}
                {(isCompleted || isFailed) && (
                  <Iconify icon={statusDisplay.icon} width={20} sx={{ color: statusDisplay.color }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  {statusDisplay.text}
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {totalListings} listing{totalListings !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* One chip per outcome that has items in it (when completed) */}
            {isCompleted && results && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {outcomeCounts
                  .filter(({ count }) => count > 0)
                  .map(({ outcome, label, count }) => (
                    <Chip
                      key={outcome}
                      size="small"
                      icon={<Iconify icon={OUTCOME_CHIPS[outcome].icon} width={16} />}
                      label={`${count} ${label}`}
                      color={OUTCOME_CHIPS[outcome].color}
                      variant="soft"
                    />
                  ))}
              </Box>
            )}

            {/* Processing hint */}
            {isProcessing && (
              <Typography variant="caption" color="text.secondary">
                You can minimize or close this. It keeps going, and the results show up when it&apos;s done.
              </Typography>
            )}
          </Stack>
        </CardContent>

        {/* Footer actions */}
        {isCompleted && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              startIcon={<Iconify icon="solar:eye-bold" />}
            >
              View Results
            </Button>
          </Box>
        )}

        {isFailed && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        )}
      </Paper>
    </Slide>
  );
}

BulkJobProgress.propTypes = {
  batchId: PropTypes.string,
  onComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Redux thunk creator: `dispatch(getStatus(batchId))` resolves to the job's status.
  getStatus: PropTypes.func.isRequired,
  copy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    processingText: PropTypes.string.isRequired,
    // The word after the count for each outcome the job can have, in the order the chips show.
    outcomes: PropTypes.shape({
      success: PropTypes.string.isRequired,
      partial: PropTypes.string,
      skipped: PropTypes.string,
      failed: PropTypes.string,
    }).isRequired,
  }).isRequired,
};
